import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { KeyRound, Save, Trash2, Eye, EyeOff, Loader2, RefreshCw, Sparkles } from "lucide-react";
import api from "@/lib/api";

const PROVIDER_META = {
  sendgrid: { label: "SendGrid API key", hint: "Transactional email (fallback for Resend)." },
  resend: { label: "Resend API key", hint: "Primary email provider." },
  pesapal_consumer_key: { label: "PesaPal · Consumer Key", hint: "PesaPal API access." },
  pesapal_consumer_secret: { label: "PesaPal · Consumer Secret", hint: "PesaPal API secret." },
  pesapal_ipn_id: { label: "PesaPal · IPN ID", hint: "Registered IPN identifier for webhooks." },
  nowpayments: { label: "NowPayments API key", hint: "Crypto checkout." },
  nowpayments_ipn_secret: { label: "NowPayments · IPN Secret", hint: "HMAC secret to verify IPN webhooks." },
  telegram_bot_token: { label: "Telegram · Bot Token", hint: "Bot for signals + auto invites." },
  telegram_channel_id: { label: "Telegram · Channel ID", hint: "Private signals channel." },
  turnstile_site_key: { label: "Cloudflare Turnstile · Site Key", hint: "Public captcha key — enables Turnstile on forms." },
  turnstile_secret: { label: "Cloudflare Turnstile · Secret", hint: "Server-side captcha verification." },
  bunny_stream: { label: "Bunny Stream API key", hint: "Gated course video." },
  license_signing_secret: { label: "License signing secret (optional)", hint: "Set this to require signed requests from your EAs/indicators. Leave empty to keep the validate endpoint open." },
};

export default function AdminApiKeys() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [revealed, setRevealed] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/api-keys");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load keys");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (provider) => {
    const value = drafts[provider];
    if (!value) {
      toast.error("Enter a value first");
      return;
    }
    setSavingKey(provider);
    try {
      await api.put(`/admin/api-keys/${provider}`, { value });
      toast.success(`${PROVIDER_META[provider]?.label || provider} saved`);
      setDrafts((d) => ({ ...d, [provider]: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSavingKey(null);
    }
  };

  const genSecret = (provider) => {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
    setDrafts((d) => ({ ...d, [provider]: hex }));
    setRevealed((s) => ({ ...s, [provider]: true }));
    toast.success("Strong secret generated — click Save to store it");
  };

  const remove = async (provider) => {
    if (!confirm(`Remove ${PROVIDER_META[provider]?.label || provider}?`)) return;
    try {
      await api.delete(`/admin/api-keys/${provider}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="admin-api-keys">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · API keys</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Integration credentials
          </h1>
          <p className="mt-2 max-w-xl text-sm text-slate-400">
            Store and rotate provider credentials in one place. Values are
            masked in the UI once saved.
          </p>
        </div>
        <button
          onClick={load}
          className="btn-edge-ghost text-xs"
          data-testid="admin-keys-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid gap-4">
        <PesapalPanel />
        {loading && (
          <div className="card-edge p-6 text-slate-500">Loading…</div>
        )}
        {!loading &&
          rows.map((r, i) => {
            const meta = PROVIDER_META[r.provider] || {
              label: r.provider,
              hint: "",
            };
            const shown = revealed[r.provider];
            return (
              <motion.div
                key={r.provider}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="card-edge p-5"
                data-testid={`api-key-row-${r.provider}`}
              >
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-300">
                      <KeyRound size={16} />
                    </span>
                    <div>
                      <p className="font-display text-base font-medium text-white">
                        {meta.label}
                      </p>
                      <p className="text-xs text-slate-500">{meta.hint}</p>
                    </div>
                  </div>
                  <span
                    className={`rounded-full border px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-widest ${
                      r.configured
                        ? "border-emerald-400/25 bg-emerald-400/10 text-emerald-300"
                        : "border-slate-500/25 bg-slate-500/10 text-slate-400"
                    }`}
                  >
                    {r.configured ? "configured" : "not set"}
                  </span>
                </div>

                <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
                  <div className="relative">
                    <input
                      type={shown ? "text" : "password"}
                      value={drafts[r.provider] || ""}
                      onChange={(e) =>
                        setDrafts((d) => ({
                          ...d,
                          [r.provider]: e.target.value,
                        }))
                      }
                      placeholder={
                        r.configured
                          ? `Current: ${r.masked} — enter new value to replace`
                          : "Paste new secret value"
                      }
                      data-testid={`api-key-input-${r.provider}`}
                      className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2.5 pr-11 text-sm text-white placeholder:text-slate-600 focus:border-emerald-500/60 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setRevealed((s) => ({ ...s, [r.provider]: !s[r.provider] }))
                      }
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-slate-500 hover:bg-white/5 hover:text-slate-300"
                      aria-label="Toggle reveal"
                    >
                      {shown ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    {r.provider === "license_signing_secret" && (
                      <button
                        onClick={() => genSecret(r.provider)}
                        data-testid={`api-key-generate-${r.provider}`}
                        className="btn-edge-ghost text-xs"
                        title="Generate a strong random secret"
                      >
                        <Sparkles size={14} /> Generate
                      </button>
                    )}
                    <button
                      onClick={() => save(r.provider)}
                      disabled={savingKey === r.provider}
                      data-testid={`api-key-save-${r.provider}`}
                      className="btn-edge text-xs"
                    >
                      {savingKey === r.provider ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Save size={14} />
                      )}
                      Save
                    </button>
                    {r.configured && (
                      <button
                        onClick={() => remove(r.provider)}
                        data-testid={`api-key-remove-${r.provider}`}
                        className="rounded-full border border-white/10 p-2 text-slate-400 transition-colors hover:border-red-500/40 hover:text-red-300"
                        title="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
      </div>
    </div>
  );
}

function PesapalPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/pesapal/readiness").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const register = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pesapal/register-ipn");
      toast.success(`IPN registered: ${data.ipn_id.slice(0, 8)}…`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Register failed");
    } finally { setBusy(false); }
  };

  const Dot = ({ ok }) => (
    <span className={`inline-block h-2 w-2 rounded-full ${ok ? "bg-[color:var(--evergreen)]" : "bg-[color:var(--flare)]"}`} />
  );

  return (
    <div className="card-moss p-5" data-testid="pesapal-panel">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="font-display text-base font-semibold text-[color:var(--ink)]">PesaPal · IPN readiness</p>
          <p className="text-xs text-[color:var(--ink-muted)]">Register your backend IPN URL to obtain an ipn_id, then it's used as notification_id on every transaction.</p>
        </div>
        <button onClick={register} disabled={busy} data-testid="pesapal-register-ipn" className="btn-edge text-xs">
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Register IPN"}
        </button>
      </div>
      {status && (
        <div className="grid gap-1.5 text-xs text-[color:var(--ink-body)]">
          <span className="flex items-center gap-2"><Dot ok={status.consumer_key} /> Consumer key</span>
          <span className="flex items-center gap-2"><Dot ok={status.consumer_secret} /> Consumer secret</span>
          <span className="flex items-center gap-2"><Dot ok={status.token_ok} /> Auth token reachable</span>
          <span className="flex items-center gap-2"><Dot ok={status.ipn_id} /> IPN registered (ipn_id)</span>
          <span className="mt-1 text-[color:var(--ink-muted)]">Public backend URL: {status.public_backend_url || "not set"}</span>
        </div>
      )}
    </div>
  );
}
